import { create } from 'zustand';
import type {
  BugDetail,
  BugReviewBundle,
  BugReviewItem,
  ChatMessage,
  JiraTransition,
} from '@sr-test-wizard/shared';
import { bugReviewApi } from '../lib/bugReviewApi';

interface BugReviewState {
  runId: string | null;
  bundle: BugReviewBundle | null;
  loading: boolean;
  error: string | null;
  /** Currently selected bug for the right pane. */
  selectedKey: string | null;
  detailByKey: Record<string, BugDetail | undefined>;
  detailLoading: boolean;
  detailError: string | null;
  chatByKey: Record<string, ChatMessage[] | undefined>;
  chatBusy: boolean;
  transitionsByKey: Record<string, JiraTransition[] | undefined>;
  postingComment: boolean;
  /** Filter applied to the list rail (All / Non-done / Done). */
  filter: 'all' | 'open' | 'done';

  load: (runId: string, options?: { force?: boolean }) => Promise<void>;
  select: (bugKey: string) => Promise<void>;
  refreshDetail: (bugKey: string) => Promise<void>;
  sendChat: (bugKey: string, question: string) => Promise<void>;
  draftComment: (bugKey: string, intent?: string) => Promise<string>;
  postComment: (bugKey: string, bodyMd: string) => Promise<void>;
  loadTransitions: (bugKey: string) => Promise<void>;
  transition: (bugKey: string, transitionId: string, bodyMd?: string) => Promise<void>;
  repullSource: (bugKey: string) => Promise<{ orderKey: string; bytes: number }>;
  uploadImage: (file: File) => Promise<{ name: string; size: number; url: string }>;
  applyBugUpdate: (bugKey: string, patch: Partial<BugReviewItem>) => void;
  setFilter: (filter: 'all' | 'open' | 'done') => void;
  clear: () => void;
}

/**
 * Bug review store (rail "Extras" section). Detail / chat / transitions are lazy-loaded per bug so a
 * TEXEC with 50 bugs doesn't hammer Jira on mount. The list is cached at
 * the store level and refreshed on explicit user action (or via the
 * `bug-update` WS event that the GlobalLogTray patches in).
 */
export const useBugReviewStore = create<BugReviewState>((set, get) => ({
  runId: null,
  bundle: null,
  loading: false,
  error: null,
  selectedKey: null,
  detailByKey: {},
  detailLoading: false,
  detailError: null,
  chatByKey: {},
  chatBusy: false,
  transitionsByKey: {},
  postingComment: false,
  filter: 'open',

  async load(runId, options) {
    const force = options?.force ?? false;
    const state = get();
    if (!force && state.bundle && state.runId === runId) return;
    const isDifferentRun = state.runId !== null && state.runId !== runId;
    set({
      runId,
      loading: true,
      error: null,
      ...(isDifferentRun
        ? {
            bundle: null,
            selectedKey: null,
            detailByKey: {},
            chatByKey: {},
            transitionsByKey: {},
          }
        : {}),
    });
    try {
      const bundle = force
        ? await bugReviewApi.refresh(runId)
        : await bugReviewApi.list(runId);
      set({ bundle });
    } catch (err) {
      set({ error: (err as Error).message });
    } finally {
      set({ loading: false });
    }
  },

  async select(bugKey) {
    const { runId } = get();
    if (!runId) return;
    set({ selectedKey: bugKey, detailLoading: true, detailError: null });
    try {
      const [detail, chat] = await Promise.all([
        bugReviewApi.detail(runId, bugKey),
        bugReviewApi.listChat(runId, bugKey),
      ]);
      set((s) => ({
        detailByKey: { ...s.detailByKey, [bugKey]: detail },
        chatByKey: { ...s.chatByKey, [bugKey]: chat },
      }));
    } catch (err) {
      set({ detailError: (err as Error).message });
    } finally {
      set({ detailLoading: false });
    }
  },

  async refreshDetail(bugKey) {
    const { runId } = get();
    if (!runId) return;
    try {
      const detail = await bugReviewApi.detail(runId, bugKey);
      set((s) => ({
        detailByKey: { ...s.detailByKey, [bugKey]: detail },
      }));
    } catch (err) {
      set({ detailError: (err as Error).message });
    }
  },

  async sendChat(bugKey, question) {
    const { runId } = get();
    if (!runId) return;
    const optimistic: ChatMessage = {
      id: -Date.now(),
      role: 'user',
      content: question,
      ts: new Date().toISOString(),
    };
    set((s) => ({
      chatBusy: true,
      chatByKey: {
        ...s.chatByKey,
        [bugKey]: [...(s.chatByKey[bugKey] ?? []), optimistic],
      },
    }));
    try {
      const data = await bugReviewApi.sendChat(runId, bugKey, question);
      set((s) => ({
        chatByKey: {
          ...s.chatByKey,
          [bugKey]: [...(s.chatByKey[bugKey] ?? []), data.message],
        },
      }));
    } finally {
      set({ chatBusy: false });
    }
  },

  async draftComment(bugKey, intent) {
    const { runId } = get();
    if (!runId) return '';
    const data = await bugReviewApi.draftComment(runId, bugKey, intent);
    return data.bodyMd;
  },

  async postComment(bugKey, bodyMd) {
    const { runId } = get();
    if (!runId) return;
    set({ postingComment: true });
    try {
      const detail = await bugReviewApi.postComment(runId, bugKey, bodyMd);
      set((s) => ({
        detailByKey: { ...s.detailByKey, [bugKey]: detail },
      }));
    } finally {
      set({ postingComment: false });
    }
  },

  async loadTransitions(bugKey) {
    const { runId } = get();
    if (!runId) return;
    try {
      const transitions = await bugReviewApi.listTransitions(runId, bugKey);
      set((s) => ({
        transitionsByKey: { ...s.transitionsByKey, [bugKey]: transitions },
      }));
    } catch (err) {
      set({ detailError: (err as Error).message });
    }
  },

  async transition(bugKey, transitionId, bodyMd) {
    const { runId } = get();
    if (!runId) return;
    const detail = await bugReviewApi.transition(runId, bugKey, transitionId, bodyMd);
    set((s) => ({
      detailByKey: { ...s.detailByKey, [bugKey]: detail },
    }));
  },

  async repullSource(bugKey) {
    const { runId } = get();
    if (!runId) throw new Error('No run loaded');
    return bugReviewApi.repullSource(runId, bugKey);
  },

  async uploadImage(file) {
    const { runId } = get();
    if (!runId) throw new Error('No run loaded');
    return bugReviewApi.uploadImage(runId, file);
  },

  applyBugUpdate(bugKey, patch) {
    set((s) => {
      if (!s.bundle) return s;
      const items = s.bundle.items.map((item) =>
        item.key === bugKey ? { ...item, ...patch } : item,
      );
      return { bundle: { ...s.bundle, items } };
    });
  },

  setFilter(filter) {
    set({ filter });
  },

  clear() {
    set({
      runId: null,
      bundle: null,
      loading: false,
      error: null,
      selectedKey: null,
      detailByKey: {},
      chatByKey: {},
      transitionsByKey: {},
      filter: 'open',
    });
  },
}));
